/**
 * Open-response writing rubrics for Training Session AI grading.
 * Closed-choice ScoringRules stay on each exam's scoring.ts — do not merge.
 */

export type WritingRubricDimensionId = 'task' | 'coherence' | 'vocabulary' | 'grammar';

export type WritingRubricAggregate = 'sum' | 'average';

export type WritingRubricLevelHintStyle = 'cefr' | 'band' | 'generic';

export interface WritingRubricDimensionDef {
  id: WritingRubricDimensionId;
  label: string;
}

export interface WritingRubric {
  /** Exam id this rubric applies to (or `default`). */
  examId: string;
  /** Overall task score ceiling (e.g. 20 TCF, 9 IELTS, 5 TOEFL). */
  scoreMax: number;
  /** Per-dimension score ceiling. */
  dimensionMax: number;
  /**
   * How overall score relates to dimensions in the grader prompt.
   * `sum` ≈ TCF 4×5 → 20; `average` ≈ IELTS/TOEFL band criteria.
   */
  aggregate: WritingRubricAggregate;
  /** Short scale description for prompts / UI. */
  scaleLabel: string;
  levelHintStyle: WritingRubricLevelHintStyle;
  dimensions: WritingRubricDimensionDef[];
}

const SHARED_DIMENSIONS: WritingRubricDimensionDef[] = [
  { id: 'task', label: '任务完成' },
  { id: 'coherence', label: '连贯' },
  { id: 'vocabulary', label: '词汇' },
  { id: 'grammar', label: '语法' },
];

/** TCF Expression écrite single-task training scale. */
export const TCF_WRITING_RUBRIC: WritingRubric = {
  examId: 'tcf',
  scoreMax: 20,
  dimensionMax: 5,
  aggregate: 'sum',
  scaleLabel: '0–20（TCF Expression écrite 单任务）',
  levelHintStyle: 'cefr',
  dimensions: SHARED_DIMENSIONS,
};

/** IELTS Writing band (Task 1/2 criteria averaged to overall band). */
export const IELTS_WRITING_RUBRIC: WritingRubric = {
  examId: 'ielts',
  scoreMax: 9,
  dimensionMax: 9,
  aggregate: 'average',
  scaleLabel: '0–9（IELTS Writing Band）',
  levelHintStyle: 'band',
  dimensions: [
    { id: 'task', label: '任务回应' },
    { id: 'coherence', label: '连贯与衔接' },
    { id: 'vocabulary', label: '词汇资源' },
    { id: 'grammar', label: '语法多样性与准确性' },
  ],
};

/** TOEFL Writing independent/integrated style 0–5. */
export const TOEFL_WRITING_RUBRIC: WritingRubric = {
  examId: 'toefl',
  scoreMax: 5,
  dimensionMax: 5,
  aggregate: 'average',
  scaleLabel: '0–5（TOEFL Writing）',
  levelHintStyle: 'generic',
  dimensions: SHARED_DIMENSIONS,
};

/**
 * Fallback for exams with writing Skill but no specialized open-response scale yet
 * (DELE / SIELE / TOPIK training). Prefer explicit rubrics when added.
 */
export const DEFAULT_WRITING_RUBRIC: WritingRubric = {
  examId: 'default',
  scoreMax: 20,
  dimensionMax: 5,
  aggregate: 'sum',
  scaleLabel: '0–20（训练用表现分）',
  levelHintStyle: 'cefr',
  dimensions: SHARED_DIMENSIONS,
};

const BY_EXAM: Record<string, WritingRubric> = {
  tcf: TCF_WRITING_RUBRIC,
  ielts: IELTS_WRITING_RUBRIC,
  toefl: TOEFL_WRITING_RUBRIC,
};

export function getWritingRubric(examId: string | null | undefined): WritingRubric {
  const key = String(examId ?? '')
    .trim()
    .toLowerCase();
  if (key && BY_EXAM[key]) return BY_EXAM[key];
  return { ...DEFAULT_WRITING_RUBRIC, examId: key || 'default' };
}

/** Map a raw task score onto 0–100 performance for Training Attempt accuracy fields. */
export function normalizeWritingPerformancePercent(
  score: number | null | undefined,
  scoreMax?: number | null
): number {
  if (score == null || !Number.isFinite(score)) return 0;
  const max =
    scoreMax != null && Number.isFinite(scoreMax) && scoreMax > 0
      ? scoreMax
      : DEFAULT_WRITING_RUBRIC.scoreMax;
  return Math.max(0, Math.min(100, Math.round((score / max) * 100)));
}
